import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, IMicrositeResponse, MicrositeActionCreators } from '@nkr/store/index';
import { IBaseResponse, IMicrositeRecipient } from '@nkr/models/index';
import { Alert, Button, Col, ControlLabel, FormControl, FormGroup, Panel, Row, Table } from 'react-bootstrap';
import MicrositeHistory from './microsite-history';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

interface IProps {
    micrositeId: number,
    centerId: number,
    isAdmin: boolean,
    previousUrl: string,
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getMicrosite: (id: number, forCoach: boolean) => Promise<IMicrositeResponse>,
    decline: (id: number, editKey: string, isFixable: boolean, reason: string) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    recipient: IMicrositeRecipient,
    previousLocation: string,
    fixable: boolean,
    reason: string,
    reasonValid: boolean,
    dirty: boolean
}

class MicrositeDecline extends React.Component<TProps, IState> {
    readonly state: IState = {
        recipient: null,
        previousLocation: '',
        fixable: true,
        reason: '',
        reasonValid: true,
        dirty: false
    }

    componentDidMount = () => {
        let previousLocation = this.props.isAdmin ? `/admin/microsite/${this.props.previousUrl}` : `/center/${this.props.centerId}/microsite/${this.props.previousUrl}`;
        if (this.props.previousUrl == 'microsite/coach/approval') {
            previousLocation = 'microsite/coach/approval';
        }

        this.props.showLoading('Getting the microsite, one moment please...').then(() => {
            this.props.getMicrosite(this.props.micrositeId, false)
                .then(response => {
                    if (response.data.success) {
                        this.setState({
                            recipient: response.data.results,
                            previousLocation
                        });
                    }

                    this.props.hideLoading();
                })
                .catch((error: AxiosError) => {
                    this.props.hideLoading();
                });
        });
    }

    handleFixableChange = (fixable: boolean) => {
        this.setState({
            fixable
        });
    }

    handleReasonChange = (e: React.ChangeEvent<FormControl & HTMLTextAreaElement>) => {
        this.setState({
            reason: e.currentTarget.value
        });
    }

    handleDecline = () => {
        this.setState({
            reasonValid: this.state.reason != '',
            dirty: true
        }, () => {
            if (this.state.reasonValid) {
                let { id, editKey } = this.state.recipient;
                let { fixable, reason } = this.state;

                this.props.showLoading('Declining microsite, one moment please...').then(() => {
                    this.props.decline(id, editKey, fixable, reason)
                        .then(response => {
                            this.props.hideLoading();

                            if (response.data.success) {
                                toast.success('Microsite declined successfully, going back to previous page', {
                                    onClose: () => {
                                        window.location.href = this.state.previousLocation;
                                    }
                                });
                            }
                        })
                        .catch((error: AxiosError) => {
                            toast.error('Unable to decline the microsite, the following error has been recorded: ' + error.message);
                        });
                });
            }
        });
    }

    handleCancel = () => {
        window.location.href = this.state.previousLocation;
    }

    render = () => {
        if (this.state.recipient == null)
            return <div>One moment please...</div>;

        const { currentStatusId, customSiteStatusId, submissionLastIssue, fullName, patientStoryPending, patientWhyPending, imagesPending, logs } = this.state.recipient;

       
        const isCenter = true;

        return (
            <>
                <div className='container'>
                    {this.state.dirty && !this.state.reasonValid && (
                        <Alert bsStyle='danger'>Please correct the issues marked below...</Alert>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 20 }}>
                        <h1 style={{ margin: 0 }}>Mark as Declined</h1>
                        <div className="pull-right">
                            <Button bsStyle='link' onClick={this.handleCancel}>Return to Previous List</Button>
                        </div>
                    </div>

                    <Row>
                        <Col xs={12}>
                            <Alert bsStyle='warning'>Please be aware that text entered below will be sent to the <strong>patient</strong></Alert>
                        </Col>
                    </Row>

                    {submissionLastIssue != '' && (
                        <Row>
                            <Col xs={12}>
                                <MicrositeHistory
                                    name={fullName}
                                    logs={logs}
                                    isExpanded={false}
                                />
                            </Col>
                        </Row>
                    )}

                    <Panel>
                        <Panel.Body>
                            {isCenter && (
                                <FormGroup>
                                    <Col xs={12} sm={2} componentClass={ControlLabel}>Is the issue fixable?</Col>
                                    <Col xs={12} sm={9}>
                                        <label style={{ marginRight: 10 }}><input id='isFixableYes' name='isFixable' type='radio' checked={this.state.fixable} onClick={() => this.handleFixableChange(true)} /> Yes</label>
                                        <label><input id='isFixableNo' name='isFixable' type='radio' checked={!this.state.fixable} onClick={() => this.handleFixableChange(false)} /> No</label>
                                    </Col>
                                </FormGroup>
                            )}

                            <FormGroup validationState={this.state.dirty && !this.state.reasonValid ? 'error' : null}>
                                <Col xs={12} sm={2} componentClass={ControlLabel}>
                                    Reason for decline or<br />how they can fix it...
                                </Col>
                                <Col xs={12} sm={9}>
                                    <FormControl
                                        componentClass='textarea'
                                        cols={40}
                                        rows={10}
                                        style={{ height: 200, resize: 'none' }}
                                        value={this.state.reason}
                                        maxLength={1000}
                                        onChange={this.handleReasonChange}
                                    />
                                </Col>
                            </FormGroup>
                        </Panel.Body>
                    </Panel>

                    <FormGroup>
                        <Col xs={12} className="text-center">
                            <Button bsStyle='primary' bsSize='lg' onClick={this.handleDecline}>Mark Declined</Button>
                            <Button bsStyle='link' name='cancel' onClick={this.handleCancel}>Cancel Decline</Button>
                        </Col>
                    </FormGroup>
                </div>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => { return dispatch(AppActionCreators.showLoadingAsync(message)); },
        hideLoading: () => { return dispatch(AppActionCreators.hideLoading()); },
        getMicrosite: (id, forCoach) => { return dispatch(MicrositeActionCreators.getMicrosite(id, forCoach)); },
        decline: (id, editKey, isFixable, reason) => { return dispatch(MicrositeActionCreators.decline(id, editKey, isFixable, reason)); }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(MicrositeDecline);